// ─── src/NpdWaitlistView.tsx ─────────────────────────────────────────────────
import { useState } from "react";
import { Download, MapPin, Rocket, Search, Trash2, X } from "lucide-react";
import {
  useDeleteNpdNomination,
  useNpdNominationStats,
  useNpdNominations,
} from "./hooks/useNpd";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { NpdNomination } from "./api/npd";

const LIMIT = 20;

const CSV_HEADERS = [
  "Founder",
  "Email",
  "Startup",
  "Governorate",
  "One-Liner",
  "Submitted At",
];

function exportCsv(rows: NpdNomination[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.founderName,
      r.founderEmail,
      r.startupName,
      r.governorate,
      r.oneLiner ?? "",
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `npd_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function NpdWaitlistView() {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [page, setPage] = useState(1);

  const { data: stats } = useNpdNominationStats();
  const { data: allData } = useNpdNominations({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useNpdNominations({ page, limit: LIMIT, search, governorate });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteNpdNomination();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const govOptions = Object.keys(stats?.byGovernorate ?? {}).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="On the list"
          value={stats?.total ?? 0}
          icon={Rocket}
          accent="bg-brand-orange"
        />
        {govOptions.slice(0, 3).map((g) => (
          <StatCard
            key={g}
            label={g}
            value={stats?.byGovernorate[g] ?? 0}
            icon={MapPin}
            accent="bg-brand-teal"
          />
        ))}
      </div>

      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search founder, startup, email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={governorate}
          onChange={(e) => {
            setGovernorate(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All governorates</option>
          {govOptions.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load waitlist.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">
          No waitlist entries yet.
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Founder</th>
                  <th className="px-4 py-3 font-medium">Startup</th>
                  <th className="px-4 py-3 font-medium">Governorate</th>
                  <th className="px-4 py-3 font-medium">One-liner</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.founderName}</div>
                      <div className="text-brand-muted text-xs">{r.founderEmail}</div>
                    </td>
                    <td className="px-4 py-3 text-white">{r.startupName}</td>
                    <td className="px-4 py-3 text-brand-muted">{r.governorate}</td>
                    <td className="px-4 py-3 text-brand-muted max-w-xs truncate">
                      {r.oneLiner || "—"}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          if (window.confirm("Delete this entry?"))
                            deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
